import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PaginationState, Row, RowSelectionState, SortingState } from "@tanstack/react-table";
import { useAtomValue } from "jotai";
import clsx from "clsx";
import { DataTable } from "@/components/ui/customs/data-table/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { GetAllExpeditionBoResponseDtoExpedition } from "@/types/api/api-types";
import { ActionDataBo } from "@/types/tableAction";
import { User, userAtom } from "@/stores/authStore";
import { useGetCustoms } from "@/api/customs.api";
import {
  selectColumn,
  envoiColumn,
  identifierColumn,
  comColumn,
  pickupDateColumn,
  clientColumn,
  clientDestColumn,
  countryColumn,
  btlsVolColumn,
  packagingColumn,
  incotermColumn,
  validatedAtColumn,
  assignedToColumn,
  expeditionRowClassname,
} from "@/components/backOffice/shared/expeditionColumns";

export type RowActionsProps = {
  row: Row<GetAllExpeditionBoResponseDtoExpedition>;
  setActionData: (actionData: ActionDataBo) => void;
  user: User | null;
};

export const ExpeditionsBoxRowActions = ({ row, setActionData, user }: RowActionsProps) => {
  const { t } = useTranslation("boExpeditions");
  const expedition = row.original;

  const canFinalizeExpedition =
    !expedition.draftBy || user?.role === "SUPERADMIN" || expedition.draftBy.id === user?.userId;

  const canEditQuote = user?.role === "SUPERADMIN" || user?.role === "ADMIN";

  const canDeleteExpedition =
    user?.role === "SUPERADMIN" || user?.role === "ADMIN" || user?.role === "MEMBER";

  const notificationCount = expedition._count.notification;
  const noteCount = expedition._count.expeditionNotes;
  const hasNotification = !!expedition.notification && expedition.notification.length > 0;
  const hasUnreadNotification = hasNotification && expedition.notification?.[0]?.readById === null;

  return (
    <div className="flex flex-row gap-2 pl-30">
      <Button
        variant="default"
        disabled={!canFinalizeExpedition}
        onClick={() => setActionData({ expedition, action: "FINALIZE" })}
      >
        {t("expeditionsBoxTable.finalize")}
      </Button>
      <Button
        variant="outline"
        className={clsx(!expedition.customQuotation && "hidden")}
        disabled={!canEditQuote}
        onClick={() => setActionData({ expedition, action: "QUOTE" })}
      >
        {t("expeditionsBoxTable.editQuote")}
      </Button>
      <Button
        variant="outline"
        className={clsx(
          hasUnreadNotification && "bg-yellow-400 text-white hover:bg-yellow-500 hover:text-white",
          hasNotification &&
            !hasUnreadNotification &&
            "bg-brand600 hover:bg-brand700 text-white hover:text-white",
        )}
        onClick={() => setActionData({ expedition, action: "NOTIFICATION" })}
      >
        {t("expeditionsBoxTable.notifications")}
        <Badge variant="destructive" className={clsx(notificationCount === 0 && "hidden")}>
          {notificationCount}
        </Badge>
      </Button>
      <Button
        variant="outline"
        className={clsx(
          noteCount > 0 && "bg-brand600 hover:bg-brand700 text-white hover:text-white",
        )}
        onClick={() => setActionData({ expedition, action: "NOTES" })}
      >
        {t("expeditionsBoxTable.notes")}
        <Badge variant="destructive" className={clsx(noteCount === 0 && "hidden")}>
          {noteCount}
        </Badge>
      </Button>
      <Button variant="outline" onClick={() => setActionData({ expedition, action: "DETAIL" })}>
        {t("expeditionsBoxTable.details")}
      </Button>
      <Button
        variant="destructive"
        onClick={() => setActionData({ expedition, action: "DELETE" })}
        disabled={!canDeleteExpedition}
      >
        {t("expeditionsBoxTable.delete")}
      </Button>
    </div>
  );
};

type Props = {
  data: GetAllExpeditionBoResponseDtoExpedition[];
  rowCount: number;
  pagination: PaginationState;
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>;
  sorting: SortingState;
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
  setActionData: (actionData: ActionDataBo) => void;
  rowSelection: RowSelectionState;
  setRowSelection: React.Dispatch<React.SetStateAction<RowSelectionState>>;
  isLoading?: boolean;
};

export const ExpeditionsBoxTable = ({
  data,
  rowCount,
  pagination,
  setPagination,
  sorting,
  setSorting,
  setActionData,
  rowSelection,
  setRowSelection,
  isLoading,
}: Props) => {
  const { t } = useTranslation("boExpeditions");
  const user = useAtomValue(userAtom);
  const isAdmin = user?.role === "ADMIN" || user?.role === "SUPERADMIN";
  const canSelectRows = isAdmin || user?.role === "MEMBER";
  const { data: customs = [] } = useGetCustoms();

  const columns = useMemo(
    () => [
      ...(canSelectRows ? [selectColumn()] : []),
      assignedToColumn(setActionData, user),
      identifierColumn(setActionData, { canResetDraft: isAdmin }),
      envoiColumn(),
      comColumn(setActionData),
      pickupDateColumn({
        header: t("expeditionsBoxTable.pickupDateHeader"),
        showDeliveryBadge: true,
        showLate: true,
      }),
      clientColumn(),
      clientDestColumn(),
      countryColumn(customs),
      btlsVolColumn(),
      incotermColumn({ header: t("expeditionsBoxTable.incotermHeader") }),
      packagingColumn(),
      validatedAtColumn(),
    ],
    [setActionData, user, canSelectRows, isAdmin, customs, t],
  );

  return (
    <DataTable
      data={data}
      columns={columns}
      pagination={pagination}
      setPagination={setPagination}
      sorting={sorting}
      setSorting={setSorting}
      rowCount={rowCount}
      autoResetPageIndex={false}
      isLoading={isLoading}
      skeletonRowClassName="h-18"
      rowClassname={(row) => expeditionRowClassname(row, "h-18")}
      headerClassName="bg-gray-300"
      headCellClassName="h-12 uppercase text-xs font-semibold tracking-wide text-gray-800 py-2 px-3"
      enableRowSelection={canSelectRows}
      rowSelection={rowSelection}
      setRowSelection={setRowSelection}
      renderSubComponent={({ row }) => (
        <ExpeditionsBoxRowActions row={row} setActionData={setActionData} user={user} />
      )}
      getRowCanExpand={() => true}
    />
  );
};
